import type { AnimationData } from './types/current';
import { CURRENT_ANIMATION_DATA_VERSION } from './types/current';
import { v1ToV2 } from './v1-to-v2';
import { v2ToV3 } from './v2-to-v3';
import { v3ToV4 } from './v3-to-v4';
import { v4ToV5 } from './v4-to-v5';
import { v5ToV6 } from './v5-to-v6';
import { v6ToV7 } from './v6-to-v7';
import { v7ToV8 } from './v7-to-v8';
import { v8ToV9 } from './v8-to-v9';
import { v9ToV10 } from './v9-to-v10';

import { FRESH_ANIMATION_DATA } from 'js/src/components/animated-explainer-videos/constants';
import { ErrorReporter } from 'js/src/libs/error-reporter';

export { CURRENT_ANIMATION_DATA_VERSION } from './types/current';

// Migration ladder. Index N runs V(N+1) → V(N+2). To add a step, invoke the
// `animation-data-migration` skill. `any` is intentional — heterogeneous
// signatures; safety comes from array order.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MIGRATIONS: ReadonlyArray<(input: any) => any> = [
    v1ToV2,
    v2ToV3,
    v3ToV4,
    v4ToV5,
    v5ToV6,
    v6ToV7,
    v7ToV8,
    v8ToV9,
    v9ToV10,
];

if (MIGRATIONS.length !== CURRENT_ANIMATION_DATA_VERSION - 1) {
    throw new Error(
        `Migration ladder is out of sync: ${MIGRATIONS.length} step(s) but ` +
            `CURRENT_ANIMATION_DATA_VERSION = ${CURRENT_ANIMATION_DATA_VERSION}. ` +
            `Add the missing migration step or fix the version constant.`,
    );
}

export interface MigrationResult {
    data: AnimationData;
    /** Version detected on the input — `null` if the blob was unrecognisable. */
    fromVersion: number | null;
    didUpgrade: boolean;
}

function detectVersion(raw: unknown): number | null {
    if (typeof raw !== 'object' || raw === null) return null;
    const version = (raw as Record<string, unknown>).version;
    if (typeof version !== 'number') return null;
    return version;
}

function reportMigrationError(error: unknown, extra: Record<string, unknown>): void {
    ErrorReporter.capture(error instanceof Error ? error : new Error(String(error)), {
        level: 'warning',
        extra: { context: 'animation_data_migration', ...extra },
    });
}

// Migrate a raw animation_data blob to the current shape. Best-effort:
// unrecognisable version → fresh fallback + Sentry; otherwise walk the
// ladder and cast.
export function migrateAnimationData(raw: unknown): MigrationResult {
    const fromVersion = detectVersion(raw);

    if (fromVersion === null) {
        reportMigrationError(new Error('animation_data has no recognisable version'), {
            rawType: typeof raw,
        });
        return { data: { ...FRESH_ANIMATION_DATA }, fromVersion: null, didUpgrade: false };
    }

    if (fromVersion > CURRENT_ANIMATION_DATA_VERSION) {
        // Stale-client read of a future blob; pass through but don't save back.
        reportMigrationError(new Error(`animation_data from future version ${fromVersion}`), {
            fromVersion,
        });
    }

    let data: unknown = raw;
    for (let v = fromVersion; v < CURRENT_ANIMATION_DATA_VERSION; v++) {
        const step = MIGRATIONS[v - 1];
        try {
            data = step(data);
        } catch (error) {
            reportMigrationError(error, { step: `v${v}-to-v${v + 1}`, fromVersion });
            return { data: { ...FRESH_ANIMATION_DATA }, fromVersion, didUpgrade: false };
        }
    }

    return {
        data: data as AnimationData,
        fromVersion,
        didUpgrade: fromVersion < CURRENT_ANIMATION_DATA_VERSION,
    };
}
